import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { AdminAPI } from '@shared/types/api';

/**
 * Hook to fetch all events (admin only)
 */
export function useAdminEvents() {
  return useQuery({
    queryKey: ['admin-events'],
    queryFn: async (): Promise<AdminAPI.GetEventsResponse> => {
      return api.get('/admin/events');
    },
  });
}

/**
 * Hook to fetch a specific event with details (admin only)
 */
export function useAdminEventDetails(eventId: string | undefined) {
  return useQuery({
    queryKey: ['admin-event', eventId],
    queryFn: async (): Promise<AdminAPI.GetEventDetailsResponse> => {
      if (!eventId) throw new Error('Event ID is required');
      return api.get(`/admin/events/${eventId}`);
    },
    enabled: !!eventId,
  });
}

/**
 * Hook to fetch just the tables for an event (lightweight refresh)
 */
export function useAdminEventTables(eventId: string | undefined) {
  return useQuery({
    queryKey: ['admin-event-tables', eventId],
    queryFn: async () => {
      if (!eventId) throw new Error('Event ID is required');
      return api.get(`/admin/events/${eventId}/tables`);
    },
    enabled: !!eventId,
  });
}

/**
 * Hook to create a new event (admin only)
 */
export function useCreateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AdminAPI.CreateEventRequest): Promise<AdminAPI.CreateEventResponse> => {
      return api.post('/admin/events', data);
    },
    onSuccess: () => {
      // Invalidate admin events to refetch updated list
      queryClient.invalidateQueries({ queryKey: ['admin-events'] });
    },
  });
}

/**
 * Hook to update an event (admin only)
 */
export function useUpdateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      data 
    }: { 
      id: string; 
      data: AdminAPI.UpdateEventRequest 
    }): Promise<AdminAPI.UpdateEventResponse> => {
      return api.patch(`/admin/events/${id}`, data);
    },
    onSuccess: (_, variables) => {
      // Invalidate both the list and the specific event
      queryClient.invalidateQueries({ queryKey: ['admin-events'] });
      queryClient.invalidateQueries({ queryKey: ['admin-event', variables.id] });
    },
  });
}

/**
 * Hook to delete an event (admin only)
 */
export function useDeleteEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<AdminAPI.DeleteEventResponse> => {
      return api.delete(`/admin/events/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-events'] });
    },
  });
}

/**
 * Hook to generate automatic matches for an event (admin only)
 */
export function useGenerateMatches(eventId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params?: { systemInstructions?: string }): Promise<AdminAPI.GenerateMatchesResponse> => {
      if (!eventId) throw new Error('Event ID is required');
      return api.post(`/admin/events/${eventId}/match`, params);
    },
    onSuccess: () => {
      // Invalidate event details to refetch assignments
      queryClient.invalidateQueries({ queryKey: ['admin-event', eventId] });
    },
  });
}

/**
 * Unified hook for every table-membership change on a booking.
 * Backend handles state transitions (chat sync, broadcasts, entitlement
 * re-activation, status changes) consistently regardless of the action.
 *
 * Pass `eventId` so the matching-page caches get invalidated on success.
 */
export function useReassignBooking(eventId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bookingId,
      ...action
    }: { bookingId: string } & AdminAPI.ReassignBookingRequest): Promise<AdminAPI.ReassignBookingResponse> => {
      return api.post(`/admin/bookings/${bookingId}/reassign`, action);
    },
    onSuccess: () => {
      if (eventId) {
        queryClient.invalidateQueries({ queryKey: ['admin-event', eventId] });
        queryClient.invalidateQueries({ queryKey: ['admin-event-tables', eventId] });
      }
    },
  });
}

/**
 * Batch variant of useReassignBooking: move every active booking on a table
 * to another table, unassign them all, or mark them all can't be seated
 * (refund + one apology broadcast), in one call.
 */
export function useBatchReassignTable(eventId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableId,
      ...action
    }: { tableId: string } & AdminAPI.BatchReassignTableRequest): Promise<AdminAPI.BatchReassignTableResponse> => {
      return api.post(`/admin/tables/${tableId}/reassign-all`, action);
    },
    onSuccess: () => {
      if (eventId) {
        queryClient.invalidateQueries({ queryKey: ['admin-event', eventId] });
        queryClient.invalidateQueries({ queryKey: ['admin-event-tables', eventId] });
      }
    },
  });
}

/**
 * Hook to toggle table lock status (admin only)
 */
export function useToggleTableLock() {
  return useMutation({
    mutationFn: async ({ 
      tableId, 
      isActive 
    }: { 
      tableId: string; 
      isActive: boolean 
    }): Promise<AdminAPI.ToggleTableLockResponse> => {
      return api.patch(`/admin/tables/${tableId}/lock`, { isActive });
    },
    // No automatic refetch - rely on optimistic updates in the component
  });
}

/**
 * Hook to create a new table (admin only)
 */
export function useCreateTable(eventId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AdminAPI.CreateTableRequest): Promise<AdminAPI.CreateTableResponse> => {
      return api.post('/admin/tables', data);
    },
    onSuccess: () => {
      // Refetch only tables, not full event details
      queryClient.invalidateQueries({ queryKey: ['admin-event-tables', eventId] });
    },
  });
}

/**
 * Hook to update a table (admin only)
 */
export function useUpdateTable(eventId?: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      tableId, 
      data 
    }: { 
      tableId: string; 
      data: AdminAPI.UpdateTableRequest 
    }): Promise<AdminAPI.UpdateTableResponse> => {
      return api.patch(`/admin/tables/${tableId}`, data);
    },
    onSuccess: () => {
      // Refetch only tables when eventId is provided
      if (eventId) {
        queryClient.invalidateQueries({ queryKey: ['admin-event-tables', eventId] });
      }
    },
  });
}

/**
 * Hook to delete a table (admin only)
 */
export function useDeleteTable(eventId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tableId: string): Promise<AdminAPI.DeleteTableResponse> => {
      return api.delete(`/admin/tables/${tableId}`);
    },
    onSuccess: () => {
      // Refetch only tables, not full event details
      queryClient.invalidateQueries({ queryKey: ['admin-event-tables', eventId] });
    },
  });
}

/**
 * Hook to toggle the flaggedForReview flag on a table assignment (admin only)
 */
export function useToggleAssignmentReview() {
  return useMutation({
    mutationFn: async ({ assignmentId, flaggedForReview }: { assignmentId: string; flaggedForReview: boolean }) => {
      return api.patch(`/admin/assignments/${assignmentId}/flag-review`, { flaggedForReview });
    },
  });
}

